/* 持久化 Skill 任务详情。运行中自动轮询，刷新或换设备后仍可凭 id 找回结果。 */

import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import type { SkillExecutionSummary } from "@dreamverse/contracts";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { AssetImage } from "../../components/MessagePartRenderer";
import { Button, PageShell } from "../../components/ui";
import { IconInfo, IconRetry, IconSpark } from "../../components/Icons";
import { fetchSkillExecution } from "../../services/paint-api";
import { useResource } from "../../state/resource";
import "../pages.css";

const STATUS_LABEL: Record<string, string> = {
  awaiting_confirmation: "等待确认",
  queued: "排队中",
  running: "正在画",
  succeeded: "已完成",
  failed: "失败",
  cancelled: "已取消",
};

const skillName = (key: string) =>
  key === "dream.paint.pro" || key === "dream.paint" ? "画画" : key;

export function SkillExecutionPage() {
  const { executionId = "" } = useParams();
  const data = useResource<SkillExecutionSummary>(
    (signal) => fetchSkillExecution(executionId, signal),
    [executionId],
  );
  const lastReady = useRef<SkillExecutionSummary | null>(null);
  if (data.state.phase === "ready") lastReady.current = data.state.data;
  const task =
    data.state.phase === "ready" ? data.state.data : lastReady.current;

  useEffect(() => {
    if (
      data.state.phase !== "ready" ||
      (data.state.data.status !== "queued" && data.state.data.status !== "running")
    ) return;
    const timer = window.setTimeout(data.reload, 2_500);
    return () => window.clearTimeout(timer);
  }, [data.reload, data.state.phase, task?.status]);

  const progress = Math.max(0, Math.min(1, task?.progress ?? 0));

  return (
    <PageShell
      icon={<IconSpark size={20} />}
      title="任务详情"
      help={<p>画画会在后台继续。你可以关掉这一页，之后再回来查看结果。</p>}
      actions={
        data.state.phase === "failed" ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重新读取
          </Button>
        ) : undefined
      }
    >
      <p className="inline-hint">
        <IconInfo size={13} />
        任务编号 <code>{executionId || "（缺失）"}</code>
      </p>

      {data.state.phase === "loading" && !task ? (
        <LoadingState label="正在读取任务状态" />
      ) : null}

      {data.state.phase === "failed" ? (
        <PageNotice
          scene="skill"
          error={data.state.error}
          onRetry={data.reload}
          expected={["任务当前进度", "本次灵力结算", "生成结果或失败原因"]}
        />
      ) : null}

      {task ? (
        <div className="skill-task">
          <div className={`skill-task__status skill-task__status--${task.status}`}>
            <span>{skillName(task.skillKey)}</span>
            <strong>{STATUS_LABEL[task.status] ?? task.status}</strong>
          </div>

          {task.status === "queued" || task.status === "running" ? (
            <div className="skill-task__progress" aria-label={`进度 ${Math.round(progress * 100)}%`}>
              <span style={{ width: `${Math.max(8, progress * 100)}%` }} />
              <p>正在后台生成，页面会自动刷新。</p>
            </div>
          ) : null}

          {task.outputAssetId ? (
            <div className="skill-task__image">
              <AssetImage assetId={task.outputAssetId} alt="画画任务生成结果" />
            </div>
          ) : null}

          {task.summary ? (
            <p className={task.status === "failed" ? "skill-task__error" : "skill-task__summary"}>
              {task.summary}
            </p>
          ) : null}

          <dl className="kv-list">
            <div>
              <dt>灵力</dt>
              <dd>
                {task.status === "failed"
                  ? (task.settledPowerCost ?? 0) > 0
                    ? "退款待处理"
                    : "已退回"
                  : task.status === "cancelled"
                    ? "未扣除"
                    : task.status === "succeeded"
                      ? task.settledPowerCost ?? "—"
                      : task.estimatedPowerCost ?? "—"}
              </dd>
            </div>
            <div>
              <dt>创建时间</dt>
              <dd>{new Date(task.createdAt).toLocaleString("zh-CN")}</dd>
            </div>
          </dl>

          <div className="skill-task__actions">
            <Link className="btn btn--quiet" to="/">
              回到对话
            </Link>
          </div>
        </div>
      ) : null}
    </PageShell>
  );
}
